import { Chip, Tooltip } from "@mui/material";
import type { Availability, AvailabilityState } from "@shared/content-rights";
import { tokens } from "../../theme";

/**
 * What a Distribution list says instead of "Published" (`BE-7`, `BE-15`).
 *
 * There is no publish state in this catalogue and there is never going to be
 * one: an asset is live the instant it is POSTed. The three things that looked
 * like one were all theatre - `CMSAsset.isPublished` in the legacy portal is a
 * hardcoded `return true` (its PUBLISHED column is always ticked),
 * `Media.enabled` is required on write and read by nothing, and
 * `ContentChannel.isPublic` filters a list while a non-public channel still
 * plays.
 *
 * So the column shows the one thing with storage, CRUD and an enforcement path
 * that actually runs: the rights window. Which means the honest states are five,
 * not two - and two of them are about what we KNOW rather than about the row:
 *
 *  - `unrestricted` - no rights row at all, which upstream means "available
 *    everywhere, forever". That is the import default and not a decision
 *    anybody made, so it is drawn differently from a licence somebody authored.
 *  - `unknown` - the caller passed something with no `contentRights` key. Never
 *    "fine" and never "broken".
 */

const LABELS: Record<AvailabilityState, string> = {
  unknown: "Rights not loaded",
  unrestricted: "Unrestricted",
  available: "Available",
  scheduled: "Scheduled",
  expired: "Expired",
};

const COLORS: Record<AvailabilityState, string> = {
  unknown: tokens.inkFaint,
  unrestricted: tokens.inkMuted,
  available: tokens.success,
  scheduled: tokens.warning,
  expired: tokens.error,
};

function detail(availability: Availability): string {
  switch (availability.state) {
    case "unknown":
      return "This view did not load the licence windows, so nothing can be said about availability.";
    case "unrestricted":
      return "No licence window exists, which upstream means available everywhere, forever. That is the import default rather than a decision.";
    case "available":
      return availability.availableIn.length
        ? `Licensed right now in ${availability.availableIn.join(", ").toUpperCase()}.`
        : "Licensed right now.";
    case "scheduled":
      return availability.nextChangeAt
        ? `Not licensed yet. The first window opens ${new Date(
            availability.nextChangeAt
          ).toLocaleString()}.`
        : "Not licensed yet.";
    case "expired":
      return "Every licence window has closed.";
    default:
      return "";
  }
}

export default function AvailabilityChip({
  availability,
  size = "small",
}: {
  availability: Availability;
  size?: "small" | "medium";
}) {
  const state = availability.state;
  const next = availability.nextChangeAt;
  return (
    <Tooltip
      title={
        detail(availability) +
        (next && state === "available"
          ? ` Changes ${new Date(next).toLocaleString()}.`
          : "")
      }
    >
      <Chip
        size={size}
        variant="outlined"
        label={LABELS[state]}
        data-testid={`availability-${state}`}
        sx={{
          borderColor: COLORS[state],
          color: COLORS[state],
          fontWeight: 600,
          fontSize: 11,
        }}
      />
    </Tooltip>
  );
}
